/**
 * useMyPermissions — 2026-07-29 (Phase 5)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * What the current user may do, for HIDING affordances only. Every gate is
 * enforced server-side; this must never be the thing that decides.
 *
 * 🔒 FAILS CLOSED. If the request errors we report everything false, so a
 * blip hides a button rather than showing one that 403s on click. That is the
 * opposite of the backend's permission resolver, which fails OPEN to the
 * legacy role default — and deliberately so: there, failing closed would lock
 * the sales floor out mid-sale; here, the worst case is a hidden nav entry the
 * user can restore by reloading.
 *
 * 🔒 While the request is in flight `permissions` is all-false too, for the
 * same reason: showing an entry and then yanking it is worse than showing it
 * a beat late.
 *
 * Cached in module scope for the session and keyed on the caller's id, so a
 * re-login in the same tab does not inherit the previous user's answer.
 *
 * ⚠️ NEVER RENDERED. The frontend does not compile locally (~71k pre-existing
 * errors; react does not resolve), so this hook is UNVERIFIED.
 */
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { useAuthStore } from '../stores/auth';

/** Mirrors backend `PERMISSION_KEYS` (core/permissions/permission-catalog.ts). */
export type MyPermissionKey =
  | 'override_payment_confirmation'
  | 'convert_so_without_payment_proof'
  | 'reassign_leads'
  | 'field_agent'
  | 'view_field_agents'
  | 'approve_ta_da';

export type MyPermissions = Partial<Record<MyPermissionKey, boolean>> &
  Record<string, boolean | undefined>;

/** 🔒 The fail-closed answer. Never mutate — callers read it directly. */
const NONE: MyPermissions = Object.freeze({}) as MyPermissions;

let cacheKey: string | null = null;
let cached: Promise<MyPermissions> | null = null;

async function fetchMyPermissions(): Promise<MyPermissions> {
  try {
    const res = await api.get('/permissions/me');
    const p = res?.data?.data?.permissions;
    // 🔒 A non-object body (proxy error page, HTML 502) must read as "nothing",
    // not as a truthy object whose every lookup is undefined-but-present.
    if (!p || typeof p !== 'object' || Array.isArray(p)) return NONE;
    return p as MyPermissions;
  } catch {
    return NONE; // fail closed
  }
}

/**
 * Drop the cache — call after a login/logout if one is ever wired. Exported so
 * the session-scoped cache is not a dead end.
 */
export function resetMyPermissionsCache(): void {
  cacheKey = null;
  cached = null;
}

export function useMyPermissions(): { permissions: MyPermissions; loading: boolean } {
  const user = useAuthStore((s: any) => s.user);
  const userId: string = user?.id ?? 'anonymous';

  const [permissions, setPermissions] = useState<MyPermissions>(NONE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (cacheKey !== userId) {
      cacheKey = userId;
      cached = fetchMyPermissions();
    }
    setLoading(true);
    // `cached` is assigned on every miss above, so it is non-null here.
    (cached as Promise<MyPermissions>).then((p) => {
      if (!alive) return;
      setPermissions(p);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  return { permissions, loading };
}

/** `true` only when explicitly granted. Undefined, null and errors all read false. */
export function can(permissions: MyPermissions, key: string): boolean {
  return permissions[key] === true;
}
